import { onSaveEvent, save } from "./Save";
import type { AchievementEvent } from "./achievementDefs";
import { QUEST_DEFS, isQuestLevelUnlocked, questProgress, type QuestDef } from "./questDefs";

class QuestSystem {
  private initialized = false;
  private completeListeners: ((quest: QuestDef) => void)[] = [];

  init(): void {
    if (this.initialized) return;
    this.initialized = true;
    onSaveEvent((event) => this.notify(event));
    this.reconcile();
  }

  onComplete(fn: (quest: QuestDef) => void): void { this.completeListeners.push(fn); }
  notifyRunEnd(stats: Extract<AchievementEvent, { kind: "runEnd" }>['stats']): void {
    this.notify({ kind: "runEnd", stats });
  }
  /** Applies achievements earned before Quest Mode and repairs interrupted saves. */
  reconcile(): void { this.notify(null); }

  private notify(event: AchievementEvent | null): void {
    const data = save.get();
    // Cloud saves may predate Quest Mode and bypass LocalSaveStore's merge.
    // Keep this additive so opening the board safely migrates them in place.
    data.quests ??= { completed: {}, baselines: {} };
    for (const quest of QUEST_DEFS) {
      if (data.quests.completed[quest.id]) continue;
      const unlocked = isQuestLevelUnlocked(quest.level, data);
      if (quest.target != null) {
        // While locked, pin the baseline to the current raw stat so stats
        // earned before the quest unlocks don't count toward its target.
        if (!unlocked) {
          const raw = quest.check(event, data);
          if (typeof raw === "number") data.quests.baselines[quest.id] = raw;
          continue;
        }
        if (questProgress(quest, data) < quest.target) continue;
      } else {
        if (!unlocked) continue;
        if (quest.check(event, data) !== true) continue;
      }
      data.quests.completed[quest.id] = Date.now();
      save.addXp(quest.reward.xp);
      if (quest.reward.skillPoints) data.skillPoints += quest.reward.skillPoints;
      save.markDirty();
      for (const fn of this.completeListeners) fn(quest);
    }
  }
}

export const quests = new QuestSystem();
